export interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
  duration?: number | null; // null or 0 means persistent (no auto-dismiss)
  downloadUrl?: string; // Optional download link for export notifications
  filename?: string; // Optional filename for export notifications
}

type NotificationHandler = (notifications: Notification[]) => void;

class NotificationService {
  private notifications: Notification[] = [];
  private handlers: Set<NotificationHandler> = new Set();
  private nextId = 1;

  addNotification(notification: Omit<Notification, 'id'>): void {
    const newNotification: Notification = {
      ...notification,
      id: (this.nextId++).toString(),
      duration:
        notification.duration !== undefined ? notification.duration : 3000,
    };

    this.notifications.push(newNotification);
    this.notifyHandlers();

    // Auto-remove notification after duration (if duration > 0)
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        this.removeNotification(newNotification.id);
      }, newNotification.duration);
    }
  }

  removeNotification(id: string): void {
    this.notifications = this.notifications.filter((n) => n.id !== id);
    this.notifyHandlers();
  }

  success(message: string, duration?: number | null): void {
    this.addNotification({ message, type: 'success', duration });
  }

  error(message: string, duration?: number | null): void {
    this.addNotification({ message, type: 'error', duration });
  }

  info(message: string, duration?: number | null): void {
    this.addNotification({ message, type: 'info', duration });
  }

  exportCompleted(
    message: string,
    downloadUrl: string,
    filename: string
  ): void {
    this.addNotification({
      message,
      type: 'success',
      duration: null, // permanent
      downloadUrl,
      filename,
    });
  }

  clear(): void {
    this.notifications = [];
    this.notifyHandlers();
  }

  subscribe(handler: NotificationHandler): () => void {
    this.handlers.add(handler);
    // Immediately call with current notifications
    handler(this.notifications);

    // Return unsubscribe function
    return () => {
      this.handlers.delete(handler);
    };
  }

  private notifyHandlers(): void {
    this.handlers.forEach((handler) => {
      try {
        handler([...this.notifications]);
      } catch (error) {
        console.error('Error in notification handler:', error);
      }
    });
  }
}

export const notificationService = new NotificationService();
